import { jsPDF } from 'jspdf';
import type { SessionDetail } from './api';
import { sttUnrecognizedDisplayText } from './stt-labels';

function time(sec: number) {
  const m = Math.floor(sec / 60);
  const s = Math.floor(sec % 60);
  return `${m}:${String(s).padStart(2, '0')}`;
}

export type SessionReportPdfOptions = {
  /** When false, the Cost overview card is omitted (e.g. Customer without finance.view). */
  includeCost?: boolean;
};

export function downloadSessionReportPdf(detail: SessionDetail, options?: SessionReportPdfOptions) {
  const includeCost = options?.includeCost !== false;
  const doc = new jsPDF({ unit: 'pt', format: 'a4' });
  const pageW = doc.internal.pageSize.getWidth();
  const margin = 40;
  let y = 48;

  const brand = [9, 104, 240] as const;
  const navy = [15, 47, 106] as const;
  const muted = [113, 130, 152] as const;

  // Header band
  doc.setFillColor(...brand);
  doc.rect(0, 0, pageW, 86, 'F');
  doc.setTextColor(255, 255, 255);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(20);
  doc.text('Igentify Pulse', margin, 36);
  doc.setFontSize(11);
  doc.setFont('helvetica', 'normal');
  doc.text('Session intelligence report', margin, 56);
  doc.setFontSize(9);
  doc.text(detail.sessionId, margin, 72);

  y = 110;
  doc.setTextColor(...navy);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(16);
  doc.text('Session overview', margin, y);
  y += 18;

  const user = detail.userName || detail.userEmail || 'Participant';
  const duration = Number(detail.durationSec) || 0;
  const mood = detail.perceptionMood || '—';
  const quality = detail.qualityScore != null ? `${Math.round(Number(detail.qualityScore))}%` : '—';

  const cards: Array<[string, string]> = [
    ['Participant', user],
    ['Agent', String(detail.agentId || 'default')],
    ['Started', detail.createdAt ? new Date(detail.createdAt).toLocaleString() : '—'],
    ['Duration', time(duration)],
    ['Outcome', String(detail.displayStatus || detail.status || '—')],
    ...(includeCost
      ? [['Cost', detail.costUsd != null ? `$${Number(detail.costUsd).toFixed(2)}` : '—'] as [string, string]]
      : []),
    ['Quality', quality],
    ['Perception', String(mood)],
  ];

  const cardW = (pageW - margin * 2 - 12) / 2;
  cards.forEach(([label, value], i) => {
    const col = i % 2;
    const row = Math.floor(i / 2);
    const x = margin + col * (cardW + 12);
    const cy = y + row * 52;
    doc.setFillColor(243, 247, 253);
    doc.roundedRect(x, cy, cardW, 44, 6, 6, 'F');
    doc.setTextColor(...muted);
    doc.setFontSize(8);
    doc.setFont('helvetica', 'bold');
    doc.text(label.toUpperCase(), x + 12, cy + 16);
    doc.setTextColor(...navy);
    doc.setFontSize(11);
    doc.setFont('helvetica', 'bold');
    doc.text(String(value).slice(0, 42), x + 12, cy + 32);
  });
  y += Math.ceil(cards.length / 2) * 52 + 24;

  // Perception strip
  const labels = Array.isArray((detail.perceptionAnalysis as { affect_labels?: string[] } | null)?.affect_labels)
    ? ((detail.perceptionAnalysis as { affect_labels: string[] }).affect_labels)
    : (detail.keyMoments || []).map((m) => m.label);
  if (labels.length) {
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(14);
    doc.setTextColor(...navy);
    doc.text('Perception signals', margin, y);
    y += 14;
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    doc.setTextColor(...muted);
    const line = labels.join(' · ');
    const wrapped = doc.splitTextToSize(line, pageW - margin * 2);
    doc.text(wrapped, margin, y);
    y += wrapped.length * 12 + 16;
  }

  // Transcript
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(14);
  doc.setTextColor(...navy);
  doc.text('Transcript', margin, y);
  y += 16;

  const rows = (detail.transcripts?.length
    ? detail.transcripts
    : (detail.turns || []).map((t) => ({ role: t.role, text: t.text, startMs: t.startMs }))) || [];

  for (const row of rows) {
    const speaker = row.role === 'user' ? (user.split(' ')[0] || 'User') : 'Agent';
    const stamp = time(Math.round((row.startMs || 0) / 1000));
    const body = sttUnrecognizedDisplayText(
      row.text,
      (row as { unrecognized?: boolean }).unrecognized,
    );
    if (!body) continue;
    const block = doc.splitTextToSize(`${stamp}  ${speaker}: ${body}`, pageW - margin * 2);
    if (y + block.length * 12 > doc.internal.pageSize.getHeight() - 48) {
      doc.addPage();
      y = 48;
    }
    doc.setFillColor(row.role === 'user' ? 240 : 232, row.role === 'user' ? 244 : 242, row.role === 'user' ? 250 : 255);
    doc.roundedRect(margin - 4, y - 10, pageW - margin * 2 + 8, block.length * 12 + 14, 4, 4, 'F');
    doc.setTextColor(...navy);
    doc.setFontSize(9);
    doc.setFont('helvetica', 'normal');
    doc.text(block, margin, y);
    y += block.length * 12 + 18;
  }

  // Footer
  const pages = doc.getNumberOfPages();
  for (let i = 1; i <= pages; i += 1) {
    doc.setPage(i);
    doc.setFontSize(8);
    doc.setTextColor(...muted);
    doc.text(`Igentify Pulse · ${detail.sessionId} · page ${i}/${pages}`, margin, doc.internal.pageSize.getHeight() - 24);
  }

  const short = detail.sessionId.replace(/^lk-/, '').slice(0, 8);
  doc.save(`pulse-session-${short}.pdf`);
}
